import { useFormik } from 'formik';

// import { useCallAPI } from 'hooks/useCallAPI';
import { useCampaignStore } from 'store/campaignStore';
import { useOrderStore } from 'store/orderStore';
import { BidMode, RoasStrategy } from 'core/rtbCampaign/singlePage/keyword';
import { ActionType, BudgetType } from 'core/rtbCampaign/singlePage/common';
import { useEffect, useState } from 'react';
import moment from 'moment';

interface DisplayFormProps {
  defaultForm: any;
  values: ReturnType<typeof useFormik>['values'];
  setFieldValue: ReturnType<typeof useFormik>['setFieldValue'];
}

/**
 *
 * @param initialState
 * @returns
 *
 */
const useDisplayForm = (
  initialState: DisplayFormProps = {
    defaultForm: {},
    values: {},
    setFieldValue: async (
      field: string,
      value: any,
      shouldValidate?: boolean,
    ) => Promise.resolve(),
  },
) => {
  const { defaultForm, values, setFieldValue } = initialState;
  const { order } = useOrderStore();
  const { actionType } = useCampaignStore();
  const [diffDays, setDiffDays] = useState(1);
  // 變更活動區間
  const handleDateRangeChange = (startDate, endDate) => {
    setFieldValue('basic.startDate', startDate);
    setFieldValue('basic.endDate', endDate);
  };
  const handleChangeStartDate = (startDate) => {
    setFieldValue('basic.startDate', startDate);
  };
  const handleChangeEndDate = (endDate) => {
    setFieldValue('basic.endDate', endDate);
  };
  // 變更預算類型
  const handleBudgetTypeChange = (newBudgetType) => {
    setFieldValue('basic.budgetType', newBudgetType);
    handleDResetAllBudget();
  };
  // 變更預算
  const handleBudgetChange = (newBudget) => {
    const { type, newPrice } = newBudget;
    if (type === BudgetType.DAILY) {
      setFieldValue('basic.dailyTargetBudget', newPrice);
      // 編輯模式下日預算模式有異動日預算才執行 handleAutoSetBudget
      if (
        actionType === ActionType.EDIT &&
        Number(newPrice) === defaultForm.basic.dailyTargetBudget
      ) {
        setFieldValue('basic.budget', defaultForm.basic.budget);
      } else {
        handleAutoSetBudget(newPrice);
      }
    } else if (type === BudgetType.TOTAL) {
      setFieldValue('basic.budget', newPrice);
      handleAutoSetDailyBudget(newPrice);
    }
  };
  // 自動設定每日預算
  const handleAutoSetDailyBudget = (newBudget) => {
    if (values.basic.budgetType === BudgetType.TOTAL) {
      setFieldValue('basic.dailyTargetBudget', Number(newBudget).toFixed(2));
    }
  };
  // 自動設定總預算
  const handleAutoSetBudget = (newDailyBudget) => {
    const { basic } = values;
    const start = moment(basic.startDate);
    const end = moment(basic.endDate);
    const today = moment();

    // 根據狀態選擇計算天數的基準
    const baseDate =
      actionType === (ActionType.CREATE || actionType === ActionType.COPY)
        ? start
        : start.isAfter(today)
          ? start
          : today;

    // +1 是因為同日也算 1 天
    const days = end.diff(baseDate, 'days') + 1;
    if (basic.budgetType === BudgetType.DAILY) {
      const daily = Number(newDailyBudget) || 0;
      const total = basic.expectedSpent + daily * days;
      // 四捨五入到小數第二位，並用字串表示
      const formatted = (
        Math.round((total + Number.EPSILON) * 100) / 100
      ).toFixed(2);
      setFieldValue('basic.budget', formatted);
    }
  };
  // 重置所有預算
  const handleDResetAllBudget = () => {
    setFieldValue('basic.budget', '0');
    setFieldValue('basic.dailyTargetBudget', '0');
  };
  // 切換出價選擇
  const handleBidTypeChange = (newBidMode) => {
    setFieldValue('basic.bidMode', newBidMode);
    if (newBidMode === BidMode.MANUAL) {
      setFieldValue('basic.roasStrategy', null);
    } else {
      setFieldValue('basic.roasStrategy', RoasStrategy.HIGH_ROAS);
    }
  };
  // 調整出價上線
  const handleChangeBidPrice = (newMaxBid) => {
    setFieldValue('basic.bidPrice', newMaxBid);
  };
  // 類型變更
  const handleChangeIncluded = (value) => {
    setFieldValue('basic.included', value);
  };
  // 排除指定受眾-類型變更
  const handleChangeExcluded = (value) => {
    setFieldValue('basic.excluded', value);
  };

  useEffect(() => {
    const startDate = moment(values?.basic.startDate);
    const endDate = moment(values?.basic.endDate);
    const defaultStartDate = moment(defaultForm?.basic.startDate);
    const defaultEndDate = moment(defaultForm?.basic.endDate);
    // 需要 +1 是因為當 startDate & endDate 都是同一天時 moment.js 會判定 diff 為 0,
    // 但這邊業務需求會需要是1天
    const diffDays = endDate.diff(startDate, 'days') + 1;
    setDiffDays(diffDays);
    // 編輯模式下日預算模式有異動開始與結束日期才執行 handleAutoSetBudget
    if (values.basic.budgetType === BudgetType.DAILY) {
      if (
        actionType === ActionType.EDIT &&
        startDate.isSame(defaultStartDate) &&
        endDate.isSame(defaultEndDate) &&
        defaultForm.basic.dailyTargetBudget?.toString() ===
          values.basic.dailyTargetBudget?.toString()
      ) {
        setFieldValue('basic.budget', defaultForm.basic.budget);
      } else {
        handleAutoSetBudget(values.basic.dailyTargetBudget);
      }
    }
  }, [
    values.basic.budgetType,
    values.basic.startDate,
    values.basic.endDate,
    values.basic.dailyTargetBudget,
  ]);

  return {
    order,
    diffDays,
    handleDateRangeChange,
    handleChangeStartDate,
    handleChangeEndDate,
    handleBudgetTypeChange,
    handleBudgetChange,
    handleBidTypeChange,
    handleChangeBidPrice,
    handleAutoSetBudget,
    handleChangeIncluded,
    handleChangeExcluded,
  };
};

export default useDisplayForm;
